import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SysNotifyService, TaskService, VendorsService, UserService, AuthService, RequestService, DistributionGroupService, TenantService, GmAssignmentService } from '@org/shared-services';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { TruncatePipe } from '@org/utilities';
import { VendorTask, Task, User, Role, Sublet, SubletVendorResponse, SubletVendorAssignment, TypeAheadConfig, VendorSearchResult } from '@org/interfaces';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { VendorAssociateComponent, TypeAheadComponent, ConfirmationModalTWComponent } from '@org/components';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { map } from 'rxjs/operators';


@Component({
    selector: 'app-vendor-tasks',
    imports: [
        CommonModule,
        TruncatePipe,
        VendorAssociateComponent,
        ReactiveFormsModule,
        FormsModule,
        ConfirmationModalTWComponent,
        TypeAheadComponent,
    ],
    templateUrl: './vendorTask.component.html',
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translateX(0)'
            })),
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('200ms ease-in')
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VendorTasksComponent implements OnInit, AfterViewInit {
  isLoading = false;
  isCreatePanelOpen = false;
  vendorTasks$: Observable<VendorTask[]>;
  vendors$!: Observable<boolean>;
  selectedTask: VendorTask | null = null;
  inviteVendorForm!: FormGroup;
  // Modal visibility
  isInviteVendorModalVisible = false;
  private vendorTasksSubject = new BehaviorSubject<VendorTask[]>([]);

  vendorCategories = [
    { value: 'Glass', label: 'Glass' },
    { value: 'Striping', label: 'Striping' },
    { value: 'Sensor', label: 'Sensor' },
    { value: 'Alignment', label: 'Alignment' },
    { value: 'Misc', label: 'Misc' }
  ];

  // Invite Vendor Panel Visibility

  activeTab: 'vendors' | 'tasks' = 'vendors';

  tasksWithSublets: Task[] = [];

  // Modal states
  showDeleteConfirmation = false;
  showCompleteConfirmation = false;
  showReassignConfirmation = false;
  selectedTaskForAction: Task | null = null;

  users: User[] = [];
  user: any = null;
  currentTenant: any = null;
  selectedReassignUser: string = '';

  searchTerm: string = '';
  filteredUsers: User[] = [];

  slideOutVisible2 = false;
  selectedTaskForView: Task | null = null;

  // Add these new properties
  isEditMode = false;
  requestName = '';

  updTask!: Task;
  loadingComments = false;
  isFocused = false;
  flagTextMap: { [key: string]: string } = {
    'red': 'High Priority',
    'yellow': 'Medium Priority',
    'green': 'Low Priority'
  };

  // Add these new properties
  requests: any[] = [];  // Array to hold request options
  filteredGlobalGroups: any[] = [];
  filteredPrivateGroups: any[] = [];
  globalGroups: any[] = [];
  privateGroups: any[] = [];
  tenantUsers: any[] = [];
  excludedNames: any[] = [];
  slideOutVisible3 = false;  // For mobile comment view
  slideOutVisible4 = false;  // For sublet component
  selectedEmployeeId: string = '';
  // Add activeFilter property
  activeFilter = 'vendors';  // Default value

  // Add these properties
  subletSelected: SubletVendorAssignment | null = null;
  vendors: any[] = [];

  selectedVendors: any[] = [];
  vendorRequestDetails: string = '';
  vendorRequestRONumber: string = '';
  uploadedImages: File[] = [];
  imagePreviews: string[] = [];
  searchQuery: string = '';

  subletAssignments: { [taskId: string]: SubletVendorAssignment[] } = {};

  typeaheadConfig: TypeAheadConfig<VendorSearchResult> = {
    searchFn: (term: string) => {
      if (!term) return of([]);
      return this.vendorsService.searchVendors({ query: term, limit: 5 }).pipe(
        map(response => {
          // Flatten all results from different service types into a single array
          return Object.values(response.results).flat();
        })
      );
    },
    placeholder: 'Search Vendors...',
    displayFn: (item: VendorSearchResult) => {
      if (!item) return '';
      // Format: "Tenant Names - RO123 - Glass"
      const tenants = item.tenantNames && item.tenantNames !== 'No tenants assigned' 
        ? item.tenantNames 
        : 'No Tenant';
      const roNumber = item.roNumber || 'No RO';
      const service = item.service || 'No Service';
      
      return `${tenants} - ${roNumber} - ${service}`;
    },
    valueFn: (item: VendorSearchResult) => item,
    minLength: 2,
    debounceTime: 300,
    dropdownWidth: '150%',  // Makes dropdown 50% wider than the input
    dropdownMaxHeight: '400px',
    dropdownPosition: 'right',
    showSearchIcon: true,
    iconPosition: 'left',
    onItemClick: (vendor: VendorSearchResult, event: MouseEvent) => {
     
      this.onVendorSelected(vendor);
    }
  };

  @ViewChildren('vendorTaskRow') vendorTaskRows!: QueryList<ElementRef>;
  selectedVendorTaskId: string | null = null;
  highlightedTaskId: string | null = null;

  constructor(
    private vendorsService: VendorsService,
    private taskService: TaskService,
    private requestService: RequestService,
    private userService: UserService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private sysNotifyService: SysNotifyService,
    private fb: FormBuilder,
    private distroGroupService: DistributionGroupService,
    private tenantService: TenantService,
    private gmAssignmentService: GmAssignmentService
  ) {
    this.vendorTasks$ = this.vendorTasksSubject.asObservable();
    this.vendors$ = this.vendorsService.vendors$;

     // Initialize the Invite Vendor Form
     this.inviteVendorForm = this.fb.group({
      vendorCategory: ['', Validators.required],
      contactName: ['', Validators.required],
      vendorName: [''],
      phoneNumber: ['', Validators.required],
      emailAddress: ['']
    });
  }

  ngOnInit() {
    this.user = this.authService.currentUserValue;
    if (this.user && this.user.roles.includes('gm')) {
      this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
        this.currentTenant = res.data.tenant;
        this.initAfterTenant();
      });
    } else {
      this.currentTenant = this.user?.tenant;
      this.initAfterTenant();
    }
    
    this.vendors$.subscribe((isInviteVendorModalVisible) => {
      this.isInviteVendorModalVisible = isInviteVendorModalVisible;
      this.cdr.markForCheck();
    });
   
  }

  ngAfterViewInit() {
    // No-op for now, but required for AfterViewInit
  }

  initAfterTenant() {
    this.loadVendorTasks();
    this.loadTasksWithSublets();
    this.loadUsers();
    this.filteredUsers = this.users;
    this.loadRequests();
    this.loadGroups();
  }

  loadVendorTasks() {
    this.isLoading = true;
    this.vendorsService.getVendorTasks().subscribe({
      next: (tasks: VendorTask[]) => {
        const sortedTasks = tasks.sort((a, b) => {
          return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
        });
        this.vendorTasksSubject.next(sortedTasks);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  loadTasksWithSublets() {
    this.isLoading = true;
    this.vendorsService.getTasksWithSublets().subscribe({
      next: (tasks: Task[]) => {
        this.tasksWithSublets = tasks;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading requests with sublets:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  viewTask(task: VendorTask) {
    // Implement view logic
    this.openCreateVendorTask(task);
  }

   // Add this method to handle employee selection
  onEmployeeSelect(employeeId: string) {
    if (!employeeId) return;
    
    this.selectedEmployeeId = employeeId;
    
    // Find the selected user or group
    const selectedUser = this.filteredUsers.find(u => u._id === employeeId);
    const selectedGlobalGroup = this.filteredGlobalGroups.find(g => g._id === employeeId);
    const selectedPrivateGroup = this.filteredPrivateGroups.find(g => g._id === employeeId);
    
    // Update the updTask.employee object with the found entity
    const selectedEntity = selectedUser || selectedGlobalGroup || selectedPrivateGroup;
    if (selectedEntity) {
      this.updTask.employee = selectedEntity;
    } else {
      this.updTask.employee = { _id: employeeId } as User;
    }
    
   
    this.cdr.markForCheck();
  }

  updateTaskStatus(task: VendorTask) {
    const newStatus = window.confirm('Mark as completed?') ? 'completed' : 'in-progress';
    
    this.vendorsService.updateTaskStatus(task._id as string, newStatus).subscribe({
      next: (updatedTask) => {
        const currentTasks = this.vendorTasksSubject.getValue();
        const updatedTasks = currentTasks.map(t => 
          t._id === updatedTask._id ? updatedTask : t
        );
        this.vendorTasksSubject.next(updatedTasks);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error updating task status:', error);
      }
    });
  }

  openCreateVendorTask(task?: VendorTask) {
    this.selectedTask = task || null;
    this.isCreatePanelOpen = true;
    this.cdr.markForCheck();
  }

  closeCreatePanel() {
    this.isCreatePanelOpen = false;
    this.cdr.markForCheck();
  }

  showSuccess(msg: string) {
    this.sysNotifyService.showSuccess(msg, 3000, 'right', 'top');
  }

  showError(msg: string) {
    this.sysNotifyService.showError(msg, 3000, 'right', 'top');
  }

  showWarning(msg: string) {
    this.sysNotifyService.showWarning(msg, 3000, 'right', 'top');
  }

  openInviteVendor() {
    this.isInviteVendorModalVisible = true;

  }

  // Close Modal
  closeInviteVendorModal() {
    this.isInviteVendorModalVisible = false;
    this.inviteVendorForm.reset(); // Reset the form when closing
    this.vendorsService.closeInviteVendorPanel();
  }

   // Submit Invite Vendor Form
   submitInviteVendor() {
    if (this.inviteVendorForm.valid) {
      this.vendorsService.createTempVendor(this.inviteVendorForm.value)
        .subscribe({
        next: (data) => {
          this.showSuccess('Vendor added successfully');
          this.closeInviteVendorModal();
          },
          error: (err) => {
            this.showError('Error adding vendor');
          }
      })
      
    }
   }

   setActiveTab(tab: 'vendors' | 'tasks') {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  openDeleteConfirmation(task: Task) {
    this.selectedTaskForAction = task;
    this.showDeleteConfirmation = true;
    this.cdr.markForCheck();
  }

  closeDeleteConfirmation() {
    this.showDeleteConfirmation = false;
    this.selectedTaskForAction = null;
    this.cdr.markForCheck();
  }

  confirmDelete() {
    if (this.selectedTaskForAction) {
      // Implement delete logic here
      this.taskService.deleteTask(this.selectedTaskForAction._id as string).subscribe({
        next: () => {
          this.showSuccess('Task deleted successfully');
          this.closeDeleteConfirmation();
          // Refresh task list
          this.loadTasksWithSublets();
        },
        error: (error) => {
          this.showError('Error deleting task');
        }
      });
    }
  }

  openCompleteConfirmation(task: Task) {
    this.selectedTaskForAction = task;
    this.showCompleteConfirmation = true;
    this.cdr.markForCheck();
  }

  closeCompleteConfirmation() {
    this.showCompleteConfirmation = false;
    this.selectedTaskForAction = null;
    this.cdr.markForCheck();
  }

  confirmComplete() {
    if (this.selectedTaskForAction) {
     
        const updatedTask = {
          ...this.selectedTaskForAction,
          taskCompleted: true
        };
  
      this.taskService.updateTask(this.selectedTaskForAction._id as string, updatedTask, 'Task completed', 'update').subscribe({  
        next: () => {
          this.showSuccess('Task marked as complete');
          this.closeCompleteConfirmation();
          this.loadTasksWithSublets();
        },
        error: (error) => {
          this.showError('Error marking task as complete');
        }
      }); 
    }
  }

  openReassignModal(task: Task) {
    this.selectedTaskForAction = task;
    this.selectedReassignUser = '';
    this.searchTerm = '';
    this.filterUsers(); // Reset filtered users
    this.showReassignConfirmation = true;
    this.cdr.markForCheck();
  }

  closeReassignConfirmation() {
    this.showReassignConfirmation = false;
    this.selectedTaskForAction = null;
    this.selectedReassignUser = '';
    this.searchTerm = '';
    this.cdr.markForCheck();
  }

  confirmReassign() {
    if (this.selectedTaskForAction && this.selectedReassignUser) {
      const updatedTask = {
        ...this.selectedTaskForAction,
        employee: { _id: this.selectedReassignUser } as User,
        isRead: false
      };

      this.taskService.updateTask(
        this.selectedTaskForAction._id as string,
        updatedTask,
        'Task reassigned',
        'update'
      ).subscribe({
        next: () => {
          this.showSuccess('Task reassigned successfully');
          this.closeReassignConfirmation();
          this.loadTasksWithSublets();
        },
        error: (error) => {
          this.showError('Error reassigning task');
          console.error('Error reassigning task:', error);
        }
      });
    }
  }

  filterUsers() {
    if (!this.searchTerm) {
      this.filteredUsers = this.users;
      return;
    }

    const searchLower = this.searchTerm.toLowerCase();
    this.filteredUsers = this.users.filter(user => 
      user.name.toLowerCase().includes(searchLower) || 
      user.email.toLowerCase().includes(searchLower)
    );
  }

  loadUsers() {
    this.userService.getUsersByTenant(this.getTenantContext()).subscribe({
      next: (users: User[]) => {
        const filteredUsers = users
          .filter((user: User) => !user.roles.some((role: Role) => role.name === 'developer'))
          .sort((a: User, b: User) => a.name.localeCompare(b.name));

        this.users = filteredUsers;
        this.filteredUsers = filteredUsers;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.showError('Error loading users');
        console.error('Error loading users:', error);
      }
    });
  }

 

  closeSlideOut2() {
    this.slideOutVisible2 = false;
    this.selectedTaskForView = null;
    this.cdr.markForCheck();
  }

  // Add method for comment submission
  onSubmitComment(task: Task, isNewComment: boolean, event?: Event) {
    if (event) {
      event.preventDefault();
    }
    // Implement comment submission logic here
  }

  // Update formatDate to handle undefined
  formatDate(date: string | Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  clearPlaceholder() {
    this.isFocused = true;
  }

  restorePlaceholder() {
    this.isFocused = false;
  }

  // Add these methods
  onSubmit(isNewComment = true) {
    if (this.updTask.comments) {
      this.taskService.updateTask(
        this.updTask._id as string,
        this.updTask,
        this.updTask.comments,
        'comment'
      ).subscribe({
        next: () => {
          this.showSuccess('Comment added successfully');
          this.updTask.comments = '';
          this.loadTasksWithSublets();
        },
        error: (error) => {
          this.showError('Error adding comment');
          console.error('Error adding comment:', error);
        }
      });
    }
  }

  // Update showAddSublet to handle Request object
  showAddSublet(request: any): boolean {
    if (!request) return false;
    // Add your logic here
    return true;
  }

  // Add this method to toggle the sublet component
  toggleSubletComponent() {
    this.slideOutVisible4 = !this.slideOutVisible4;
  }

 

   // Method to reset the vendor association view
   resetVendorAssociationView() {
    this.selectedVendors = [];
    this.vendorRequestDetails = '';
    this.vendorRequestRONumber = '';
    this.uploadedImages = [];
    this.imagePreviews = [];
    this.searchQuery = '';
  }

  subletSelect(sublet: SubletVendorAssignment) { 
    if (this.selectedTaskForView) {
      const taskId = this.selectedTaskForView._id as string;
      const assignment = this.subletAssignments[taskId]?.find(
        a => a.subletId === sublet.subletId
      );

      this.slideOutVisible4 = true;
      if (assignment) {
        // Use existing assignment
        this.subletSelected = assignment;
      } else {
        // Create new assignment
        this.resetVendorAssociationView();
        this.subletSelected = {
          subletId: sublet.subletId || '',
          subletName: sublet.subletName,
          isAssigned: false,
          vendor: null
        };
        // Only fetch vendors if not assigned
        this.tenantService.getAssociatedVendors(sublet.subletName).subscribe({
          next: (data: any) => {
            this.vendors = data;
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            console.error('Error fetching associated vendors:', err);
          }
        });
      }
    }
  }

  openEditCommentView(task: Task, isEditMode: boolean) {
    this.slideOutVisible3 = true;
    this.cdr.markForCheck();
  }

  acknowledgeCompletion(task: Task, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    const updatedTask = {
      ...task,
      taskCompleted: true
    };

    this.taskService.updateTask(
      task._id as string,
      updatedTask,
      'Task completed',
      'update'
    ).subscribe({
      next: () => {
        this.showSuccess('Task marked as complete');
        this.loadTasksWithSublets();
      },
      error: (error) => {
        this.showError('Error completing task');
        console.error('Error completing task:', error);
      }
    });
  }

  deleteTask(task: Task) {
    if (confirm('Are you sure you want to delete this task?')) {
      this.taskService.deleteTask(task._id as string).subscribe({
        next: () => {
          this.showSuccess('Task deleted successfully');
          this.closeSlideOut2();
          this.loadTasksWithSublets();
        },
        error: (error) => {
          this.showError('Error deleting task');
          console.error('Error deleting task:', error);
        }
      });
    }
  }

  loadRequests() {
    this.requestService.getRequests().subscribe((data: any) => {
      this.requests = data.sort((a: any, b: any) => a.name.localeCompare(b.name));
      this.cdr.markForCheck();
    });
  }

  // Add loadGroups implementation
  private loadGroups() {
    const tenantContext = this.getTenantContext();
    this.distroGroupService.getDistributionGroupsByTenant(tenantContext).subscribe({
      next: (groups) => {
        this.globalGroups = groups.data.filter((group: { isGlobal: boolean }) => group.isGlobal);
        this.privateGroups = groups.data.filter((group: { isGlobal: boolean }) => !group.isGlobal);
        this.filterOptions();
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.showError('Error loading groups');
        console.error('Error loading groups:', error);
      }
    });
  }

   // Filter options based on search term
   filterOptions() {
    const searchLower = this.searchTerm.toLowerCase();
    // Filter Global Groups
    this.filteredGlobalGroups = this.globalGroups.filter(group => 
      group.name.toLowerCase().includes(searchLower)
    );
    // Filter Private Groups
    this.filteredPrivateGroups = this.privateGroups.filter(group => 
      group.name.toLowerCase().includes(searchLower)
    );
  }

  // Add this new method
  handleSubletSelection(sublet: SubletVendorAssignment, task: Task) {
    this.selectedTaskForView = task;
    this.subletSelect(sublet);
  }

  onVendorSelected(vendor: any) {
    // Find the VendorTask for this vendor (by vendorId, roNumber, etc.)
    const vendorTasks = this.vendorTasksSubject.getValue();
    const foundTask = vendorTasks.find(task =>
      task.tenants?.some(t => t._id === vendor.id) ||
      task.roNumber === vendor.roNumber // adjust as needed
    );
    if (foundTask) {
      this.selectedVendorTaskId = foundTask._id!;
      this.highlightedTaskId = foundTask._id!;
      this.cdr.detectChanges(); // Ensure view updates
      setTimeout(() => {
        const row = this.vendorTaskRows.find(
          (el) => el.nativeElement.getAttribute('data-task-id') === foundTask._id
        );
        if (row) {
          row.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => {
          this.highlightedTaskId = null;
          this.cdr.markForCheck();
        }, 2000);
      }, 100);
    }
  }

  private getTenantContext(): string {
    if (this.user && this.user.roles && this.user.roles.includes('gm')) {
      return this.currentTenant;
    }
    return this.user?.tenant;
  }
}